/**
 * Studios service — MOCK (CT-5563 shared data layer).
 *
 * Two concerns: (1) the studios catalog with its PERMISSION TREE (used by the
 * New role wizard and the member Studios tab), and (2) a member's grants per
 * studio, where each effective grant is either role-derived (inherited from one
 * or more of the member's roles, highest level wins) or direct (pinned on the
 * member, overriding the role level). Contract TBD (ARP/backend); platform
 * envelope, snake_case bodies.
 */
import { clone, envelope, errorEnvelope, notFoundEnvelope } from '../shared/utils/mockServiceHelpers.js';
import { withMockQa } from './mockQaSwitches.js';
import { flattenPermissions, PERMISSION_LEVELS } from './seeds/studios.js';
import { resetStore, store } from './seeds/store.js';

export const GRANT_SOURCES = ['role', 'direct'];

/** Test-only: reset the shared mock store (members, roles, groups, grants). */
export const __resetMockStudiosForTests = () => resetStore();

const findStudio = (id) => store.studios.find((s) => s.id === id);
const rank = (level) => PERMISSION_LEVELS.indexOf(level);

const studioGrantsFor = (member, studio) => {
  const ids = new Set(flattenPermissions(studio.permissions).map((n) => n.id));
  const byPermission = new Map();
  member.role_ids.forEach((roleId) => {
    const role = store.roles.find((r) => r.id === roleId);
    role?.permissions
      .filter((g) => ids.has(g.permission_id))
      .forEach((g) => {
        const entry = byPermission.get(g.permission_id) ?? { level: g.level, via: [] };
        if (rank(g.level) > rank(entry.level)) entry.level = g.level;
        entry.via.push({ id: role.id, name: role.name });
        byPermission.set(g.permission_id, entry);
      });
  });
  const direct = (store.directGrants[member.id] ?? []).filter((g) => ids.has(g.permission_id));
  const direct_access = direct.length > 0 || (store.studioAccess[member.id] ?? []).includes(studio.id);
  const grants = [...byPermission].map(([permission_id, { level, via }]) => ({
    permission_id,
    level,
    source: 'role',
    pinned: false,
    via_roles: via,
  }));
  direct.forEach((g) => {
    const existing = grants.find((x) => x.permission_id === g.permission_id);
    if (existing) Object.assign(existing, { level: g.level, source: 'direct', pinned: true });
    else
      grants.push({
        permission_id: g.permission_id,
        level: g.level,
        source: 'direct',
        pinned: true,
        via_roles: [],
      });
  });
  const viaRoleIds = new Set(grants.flatMap((g) => g.via_roles.map((r) => r.id)));
  const otherMembers = store.members.filter((m) => m.id !== member.id && m.role_ids.some((id) => viaRoleIds.has(id)));
  return {
    studio_id: studio.id,
    name: studio.name,
    status: 'active',
    direct_access,
    role_names: [...new Set(grants.flatMap((g) => g.via_roles.map((r) => r.name)))],
    other_member_count: otherMembers.length,
    grants,
    counts: { added_directly: direct.length, from_roles: byPermission.size },
  };
};

/**
 * Studios catalog with permission trees.
 * TODO(CT-5563 / backend): `GET /people-access/studios` — contract TBD (ARP/backend).
 */
export const listStudios = () =>
  Promise.resolve(
    envelope({ items: clone(store.studios), total: store.studios.length }, { total: store.studios.length })
  );

/**
 * One studio with its permission tree.
 * TODO(CT-5563 / backend): `GET /people-access/studios/:id` — contract TBD (ARP/backend).
 */
export const getStudio = (id) => {
  const studio = findStudio(id);
  return Promise.resolve(studio ? envelope(clone(studio)) : notFoundEnvelope('Studio not found'));
};

/**
 * A member's effective grants for every studio (or one, via `studio_id`).
 * TODO(CT-5563 / backend): `GET /people-access/members/:id/studio-grants` — contract TBD (ARP/backend).
 */
export const getMemberStudioGrants = (member_id, { studio_id } = {}) => {
  const member = store.members.find((m) => m.id === member_id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  const studios = studio_id ? store.studios.filter((s) => s.id === studio_id) : store.studios;
  if (!studios.length) return Promise.resolve(notFoundEnvelope('Studio not found'));
  return Promise.resolve(
    envelope({ member_id, items: studios.map((s) => studioGrantsFor(member, s)) })
  );
};

/**
 * Replace the grants pinned directly on a member for ONE studio. Role-derived
 * grants are untouched; a direct grant overrides the role level for that permission.
 * TODO(CT-5563 / backend): `PUT /people-access/members/:id/studio-grants/:studio_id` — contract TBD (ARP/backend).
 */
const saveGrants = (member_id, { studio_id, grants = [] } = {}) => {
  const member = store.members.find((m) => m.id === member_id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  const studio = findStudio(studio_id);
  if (!studio) return Promise.resolve(notFoundEnvelope('Studio not found'));
  const ids = new Set(flattenPermissions(studio.permissions).map((n) => n.id));
  const bad = grants.find((g) => !ids.has(g.permission_id) || !PERMISSION_LEVELS.includes(g.level));
  if (bad)
    return Promise.resolve(
      errorEnvelope(422, 'INVALID_PERMISSION', `Invalid grant: ${bad.permission_id}`)
    );
  const others = (store.directGrants[member_id] ?? []).filter((g) => !ids.has(g.permission_id));
  store.directGrants[member_id] = [
    ...others,
    ...grants.map(({ permission_id, level }) => ({ permission_id, level })),
  ];
  return Promise.resolve(envelope({ member_id, ...studioGrantsFor(member, studio) }));
};

export const saveMemberStudioGrants = (member_id, payload) =>
  withMockQa('studios', () => saveGrants(member_id, payload));

/**
 * Grant a member access to a studio directly (no permission set yet; edit them afterwards).
 * TODO(CT-5612 / backend): `PUT /people-access/members/:id/studios/:studio_id` — contract TBD (ARP/backend).
 */
const grantStudio = (member_id, studio_id) => {
  const member = store.members.find((m) => m.id === member_id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  const studio = findStudio(studio_id);
  if (!studio) return Promise.resolve(notFoundEnvelope('Studio not found'));
  const current = store.studioAccess[member_id] ?? [];
  if (!current.includes(studio_id)) store.studioAccess[member_id] = [...current, studio_id];
  return Promise.resolve(envelope(studioGrantsFor(member, studio)));
};

export const grantMemberStudio = (member_id, studio_id) =>
  withMockQa('studios', () => grantStudio(member_id, studio_id));

/**
 * Remove the direct access to a studio. Refused (409) while a role of the member still grants it:
 * that access is removed by removing the role.
 * TODO(CT-5612 / backend): `DELETE /people-access/members/:id/studios/:studio_id` — contract TBD (ARP/backend).
 */
const removeStudioAccess = (member_id, studio_id) => {
  const member = store.members.find((m) => m.id === member_id);
  if (!member) return Promise.resolve(notFoundEnvelope('Member not found'));
  const studio = findStudio(studio_id);
  if (!studio) return Promise.resolve(notFoundEnvelope('Studio not found'));
  if (studioGrantsFor(member, studio).role_names.length) {
    return Promise.resolve(
      errorEnvelope(409, 'ROLE_ACCESS', 'This access comes from a role. Remove the role instead.')
    );
  }
  const ids = new Set(flattenPermissions(studio.permissions).map((n) => n.id));
  store.directGrants[member_id] = (store.directGrants[member_id] ?? []).filter(
    (g) => !ids.has(g.permission_id)
  );
  store.studioAccess[member_id] = (store.studioAccess[member_id] ?? []).filter((id) => id !== studio_id);
  return Promise.resolve(envelope(studioGrantsFor(member, studio)));
};

export const removeMemberStudioAccess = (member_id, studio_id) =>
  withMockQa('studios', () => removeStudioAccess(member_id, studio_id));
